import { CONFIG } from '../../../Config/config'
import { authorization } from '../../Auth/Auth'

export const searchFeaturesPFGQL = variables => {
  const gql = `
    query(
      $area: GeoJSONGeometryInput
      $layer: LayerEnum
      $countryCode: String!
      $includeAddressCount: Boolean
      $after: String
      $cityId: ID
      $filterByCityIdDistributionZone: ID
      $first: Int
      $searchCodes: [String]
      ){ searchFeatures(
          layer: $layer
          countryCode: $countryCode
          area: $area
          includeAddressCount: $includeAddressCount
          after: $after
          cityId: $cityId
          filterByCityIdDistributionZone: $filterByCityIdDistributionZone
          first: $first
          searchCodes: $searchCodes
      ) {
          totalCount
          pageInfo {endCursor hasNextPage}
          collection {
              type
              features {
                  type
                  properties
                  geometry {
                      type
                      coordinates
                  }
              }
          }
      }
    }
  `
  return callAuthorizedGraphQLEndpoint(gql, variables)
}
/**
 * Save features to PathFinder
 * @param {array} input Array of features
 */
export const addFeaturesPFGQL = input => {
  const gql = `mutation($input:AddFeaturesInput!){
    addFeatures(input: $input) {
      features {
        properties 
      }
    }
  }`
  return callAuthorizedGraphQLEndpoint(gql, { input })
}

/**
 * Delete features to PathFinder
 * @param {array} input Array of features
 */
export const deleteFeaturesPFGQL = input => {
  const gql = `mutation($input:DeleteFeaturesInput!){
    deleteFeatures(input: $input) {
      deletedCount
    }
  }`
  return callAuthorizedGraphQLEndpoint(gql, { input })
}

/**
 * Update features to PathFinder
 * @param {array} input Array of features
 */
export const updateFeaturesPFGQL = input => {
  const gql = `mutation($input:UpdateFeaturesInput!){
    updateFeatures(input: $input) {
      modifiedCount
    }
  }`
  return callAuthorizedGraphQLEndpoint(gql, { input })
}

export const searchPostcodesPFGQL = variables => {
  const gql = `
    query(
      $countryCode: String!
      $searchString: String!
      $first: Int
      $after: String
      ){ searchPostcodes(
          countryCode: $countryCode
          searchString: $searchString
          first: $first
          after: $after
      ) {
          totalCount
          pageInfo { endCursor hasNextPage }
          edges {
            node {
                type
                properties
                geometry {
                    type
                    coordinates
                }
            }
          }
      } }
  `
  return callAuthorizedGraphQLEndpoint(gql, variables)
}

/**
 * POST
 * Receives the number of households in the provided polygons for a specified country
 * @param {Array<Object>} geometries
 * @param {string} geometries[0].type Expects 'polygon'
 * @param {Array} geometries[0].coordinates Expects valid coordinates for a
 * GeoJSON Polygon object.
 *
 * @return {Promise<Array<Number|null>>} Array of households amounts, value may
 * be null if it was undeterminable.
 *
 * @example
 * getNumberOfHouseholdsInPolygonBulk(
 *   'AU',
 *   [
 *     {
 *       "type": "Polygon",
 *       "coordinates": [
 *         [
 *           [
 *             151.2341331585533,
 *             -33.883546709612204
 *           ],
 *           [
 *             151.2314294918663,
 *             -33.886111824768385
 *           ],
 *           [
 *             151.2354635342247,
 *             -33.8872874767728
 *           ],
 *           [
 *             151.2341331585533,
 *             -33.883546709612204
 *           ]
 *         ]
 *       ]
 *     }
 *   ]
 * )
 *
 * Response: [1000]
 */
export const getNumberOfHouseholdsInPolygonBulk = async (
  countryCode,
  geometries,
) => {
  if (!geometries || !geometries.length) return []
  let paramsStr = ''
  let searchStr = ''
  const variables = {}

  geometries.forEach((g, index) => {
    paramsStr += `$geometry${index}: GeoJSONGeometryInput,`
    searchStr += `findNumberHouseholds${index}: findNumberHouseholds (countryCode: "${countryCode}", geometry: $geometry${index}){
        count
      }
    `
    variables[`geometry${index}`] = g
  })
  const query = `
    query (
      ${paramsStr}
    ) {
    
      ${searchStr}
    }
  `
  var response = await callAuthorizedGraphQLEndpoint(query, variables)
  var result = await response.json()
  if (result.errors) {
    // We will carry on in a partial error scenario.
    result.errors.forEach(({ message }) => {
      var serverError = new Error(message)
      // Let the developer know what's going on.
      console.error(serverError)
    })
  }
  if (!result.data) return geometries.map(g => null)
  return geometries.map((g, index) =>
    result.data[`findNumberHouseholds${index}`] &&
    typeof result.data[`findNumberHouseholds${index}`].count == 'number'
      ? result.data[`findNumberHouseholds${index}`].count
      : null,
  )
}

/** POST
 * Receives the number of households in the provided polygons for a specified country
 * @param {String} args.countryCode
 * @param {String} args.includeAddressCount
 * @param {String} args.layer 'Postcode' or 'MissionBoundaries'
 * @param {Array<Object>} geometries
 * @param {string} geometries[0].type Expects 'polygon'
 * @param {Array} geometries[0].coordinates Expects valid coordinates for a
 * GeoJSON Polygon object.
 *
 * @return {Promise<object[]>>} Array of features (postcodes or mission boundaries) in provided polygons
 */
export const searchFeaturesBulk = async ({
  countryCode,
  geometries,
  layer = 'Postcodes',
  includeAddressCount = false,
}) => {
  const { paramsStr, searchStr, variables } = geometries.reduce(
    (acc, g, index) => {
      acc.paramsStr += `$area${index}: GeoJSONGeometryInput,`
      acc.searchStr += `searchFeatures${index}: searchFeatures (
        layer: ${layer},
        countryCode: "${countryCode}", 
        includeAddressCount: ${!!includeAddressCount},
        area: $area${index}
        ) {
          totalCount
          collection {
              type
              features {
                  type
                  properties
                  geometry {
                      type
                      coordinates
                  }
              }
          }
        }
    `
      acc.variables[`area${index}`] = g
      return acc
    },
    { paramsStr: '', searchStr: '', variables: {} },
  )

  const query = `
    query (
      ${paramsStr}
    ) {
    
      ${searchStr}
    }
  `

  var response = await callAuthorizedGraphQLEndpoint(query, variables)

  var result = await response.json()
  if (result.errors) {
    // We will carry on in a partial error scenario.
    result.errors.forEach(({ message }) => {
      var serverError = new Error(message)
      // Let the developer know what's going on.
      console.error(serverError)
    })
  }
  if (!result.data) return []
  return geometries.map((g, index) => result.data[`searchFeatures${index}`])
}

export const getDistributionZones = cityId => {
  const gql = `query($cityId: ID!) {
    distributionZones(cityId: $cityId) {
        h2h {
            areaType
            center {
                lat
                lng
            }
            editMode
            id
            imported
            radius
            type
            value
            paths
        }
        d2d {
            areaType
            center {
                lat
                lng
            }
            editMode
            id
            imported
            radius
            type
            value
            paths
        }
    }
  }`

  return callAuthorizedGraphQLEndpoint(gql, {
    cityId,
  })
}

export const updateDistributionZones = input => {
  const gql = `mutation($input: DistributionZonesInput!) {
    updateDistributionZones(input: $input) {
        success
    }
  }`

  return callAuthorizedGraphQLEndpoint(gql, {
    input,
  })
}

export const searchAndSaveIntersectedPolygons = input => {
  const gql = `mutation($input: SearchAndSaveIntersectedPolygonsInput!) {
    searchAndSaveIntersectedPolygons(input: $input) {
      endCursor
      hasNextPage 
      totalCount
    }
  }`

  return callAuthorizedGraphQLEndpoint(gql, {
    input,
  })
}

/**
 * Call Graph QL Endpoint
 * @param {string} query
 * @param {object} variables
 * @returns {Promise<object>}
 */
export const callAuthorizedGraphQLEndpoint = function(query, variables) {
  const authToken = authorization.getAPIKey()
  const body = JSON.stringify({
    query,
    variables,
  })

  return fetch(`${CONFIG.PATHFINDER_API_BASE_URL}/graphql`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`,
    },
    body,
  })
}

export const getSelfServeCostData = async campaignId => {
  const gql = `query($campaignId: ID!) {
        selfServeCampaignData(campaignId: $campaignId) {
        campaignDetailedCosts {
            distribution {
              quantity
              rate
              total
            }
            bonus {
              quantity
              rate
              total
            }
            outfit {
              quantity
              rate
              total
            }
            travel {
              quantity
              rate
              total
            }
            permit {
              quantity
              rate
              total
            }
            printing {
              quantity
              rate
              total
            }
            tax
            subtotal
            totalCost
            percentOff
            amountOff
        }
        selectedCities {
           name
           id
        }
       }
      }`

  const request = await callAuthorizedGraphQLEndpoint(gql, {
    campaignId,
  })

  const {
    data: {
      selfServeCampaignData: { selectedCities, campaignDetailedCosts },
    },
  } = await request.json()

  return { selectedCities, campaignDetailedCosts }
}

export const splitFeaturesPFGQL = async ({
  codes,
  addressCount,
  countryCode,
}) => {
  const gql = `
    query ( 
    $codes: [String] 
    $addressCount: Int
    $countryCode: String
    ) {
      splitPolygonInChunks(
        codes: $codes 
        addressCount: $addressCount
        countryCode: $countryCode
      ) {
        collection {
          type
          features {
            type
            properties
            geometry {
              type
              coordinates
            }
          }
        }
      }
    }
  `

  const fetchFeaturesForCode = async code => {
    try {
      const request = await callAuthorizedGraphQLEndpoint(gql, {
        codes: [code],
        addressCount,
        countryCode,
      })

      const res = await request.json()

      if (
        !res.data ||
        !res.data.splitPolygonInChunks ||
        !res.data.splitPolygonInChunks.collection
      ) {
        console.error('Unexpected GraphQL response structure:', res)
        return []
      }

      const {
        data: {
          splitPolygonInChunks: {
            collection: { features },
          },
        },
      } = res

      return features
    } catch (error) {
      console.error(`Error fetching features for code ${code}:`, error)
      return []
    }
  }

  const allFeatures = []

  for (const code of codes) {
    const features = await fetchFeaturesForCode(code)
    allFeatures.push(...features)
  }

  return allFeatures
}
